import { createSlice } from '@reduxjs/toolkit';
import axios from '../../utils/axios';
import { dispatch } from '../store';
import { rejectWithError } from '../dispatchError';
import {
  FeedbackAppAccessDto,
  FeedbackAppConfigurationDataState,
  FeedbackAppConfigurationDto
} from '../../@types/productConfiguration';
import { translator } from 'translation/useTranslator';

const initialState: FeedbackAppConfigurationDataState = {
  isLoading: false,
  isDeleteLoading: false,
  configuration: undefined,
  isConfigurationLoading: false
};

const slice = createSlice({
  name: 'productConfigurations',
  initialState,
  reducers: {
    // General use
    startLoading(state) {
      state.isLoading = true;
    },

    stopLoading(state) {
      state.isLoading = false;
    },

    addConfigurationSuccess(state, action) {
      state.isLoading = false;
    },

    getConfigurationSuccess(state, action) {
      state.isLoading = false;
      state.configuration = action.payload;
    },

    editConfigurationSuccess(state, action) {
      state.isLoading = false;
    }
  }
});

// Reducer
export default slice.reducer;

const feedbackAppEndpoint = '/feedback-app';
const accessConfigEndpoint = `${feedbackAppEndpoint}/access-configuration`;
const configurationEndpoint = `${feedbackAppEndpoint}/configuration`;

export function getConfiguration() {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(feedbackAppEndpoint);
      dispatch(slice.actions.getConfigurationSuccess(response.data));
      return await Promise.resolve({
        result: response,
        defaultSuccessMessage: translator?.configurationsFetched() ?? 'Configurations fetched'
      });
    } catch (error) {
      dispatch(slice.actions.stopLoading());
      return await rejectWithError({
        error: error,
        defaultErrorMessage:
          translator?.couldNotFetchConfigurations() ?? 'Could not fetch configurations'
      });
    }
  };
}

type EditAccessConfigurationProps = {
  configuration: FeedbackAppAccessDto;
};
export function editAccessConfiguration({ configuration }: EditAccessConfigurationProps) {
  dispatch(slice.actions.startLoading());
  return async () => {
    try {
      const response = await axios.put(accessConfigEndpoint, configuration);
      dispatch(slice.actions.editConfigurationSuccess(response));
      return await Promise.resolve({
        result: response,
        defaultSuccessMessage: translator?.configurationUpdated() ?? 'Configuration updated'
      });
    } catch (error: any) {
      dispatch(slice.actions.stopLoading());
      return await rejectWithError({
        error: error,
        defaultErrorMessage:
          translator?.couldNotUpdateConfiguration() ?? 'Could not update configuration'
      });
    }
  };
}

type AddAccessConfigurationProps = {
  configuration: FeedbackAppAccessDto;
};
export function addAccessConfiguration({ configuration }: AddAccessConfigurationProps) {
  dispatch(slice.actions.startLoading());
  return async () => {
    try {
      const response = await axios.post(accessConfigEndpoint, configuration);
      dispatch(slice.actions.addConfigurationSuccess(response));
      return await Promise.resolve({
        result: response,
        defaultSuccessMessage:
          translator?.accessConfigurationAdded() ?? 'Access configuration added'
      });
    } catch (error: any) {
      dispatch(slice.actions.stopLoading());
      return rejectWithError({
        error: error,
        defaultErrorMessage:
          translator?.couldNotAddAccessConfiguration() ?? 'Could not add access configuration'
      });
    }
  };
}

type AddConfigurationProps = {
  configuration: FeedbackAppConfigurationDto;
};
export function addOrUpdateConfiguration({ configuration }: AddConfigurationProps) {
  dispatch(slice.actions.startLoading());
  return async () => {
    try {
      const response = await axios.put(configurationEndpoint, configuration);
      dispatch(slice.actions.addConfigurationSuccess(response));
      return await Promise.resolve({
        result: response,
        defaultSuccessMessage: translator?.configurationSaved() ?? 'Configuration saved'
      });
    } catch (error: any) {
      dispatch(slice.actions.stopLoading());
      return rejectWithError({
        error: error,
        defaultErrorMessage:
          translator?.couldNotSaveConfiguration() ?? 'Could not save configuration'
      });
    }
  };
}

type GrantAllAccessProps = {
  configuration: FeedbackAppAccessDto;
};
export function grantAccessToAll({ configuration }: GrantAllAccessProps) {
  dispatch(slice.actions.startLoading());
  return async () => {
    try {
      const response = await axios.put(`${accessConfigEndpoint}/grant-all`, configuration);
      dispatch(slice.actions.addConfigurationSuccess(response));
      return await Promise.resolve({
        result: response,
        defaultSuccessMessage:
          translator?.accessGrantedToAll() ?? 'Access granted to all active employees'
      });
    } catch (error: any) {
      dispatch(slice.actions.stopLoading());
      return rejectWithError({
        error: error,
        defaultErrorMessage:
          translator?.couldNotGrantAccessToAll() ?? 'Could not grant access to all employees'
      });
    }
  };
}

type RevokeAllAccessProps = {
  configuration: FeedbackAppAccessDto;
};
export function revokeAccessFromAll({ configuration }: RevokeAllAccessProps) {
  dispatch(slice.actions.startLoading());
  return async () => {
    try {
      const response = await axios.put(`${accessConfigEndpoint}/revoke-all`, configuration);
      dispatch(slice.actions.addConfigurationSuccess(response));
      return await Promise.resolve({
        result: response,
        defaultSuccessMessage:
          translator?.accessRevokedFromAll() ?? 'Access revoked from all employees'
      });
    } catch (error: any) {
      dispatch(slice.actions.stopLoading());
      return rejectWithError({
        error: error,
        defaultErrorMessage:
          translator?.couldNotRevokeAccessFromAll() ?? 'Could not revoke access from all employees'
      });
    }
  };
}
